import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { AlertCircle, ChevronRight, Download, Sparkles, TrendingDown } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { Input } from "@/components/ui/Input";
import { Field } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";
import { Switch } from "@/components/ui/Switch";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  useEntityComparison,
  type EntityScenarioResult,
  type ReasonableSalaryStrategy,
} from "@/hooks/useStrategy";
import { useFamilyProfile } from "@/hooks/useProfile";
import { formatMoney } from "@/lib/utils";

export function EntityTransitionPage() {
  const family = useFamilyProfile();

  // Hire-kids levers (existing).
  const [childrenOnPayroll, setChildrenOnPayroll] = useState<string>("");
  const [wagePerChild, setWagePerChild] = useState<string>("15000");

  // Phase D levers.
  const [wiPte, setWiPte] = useState<boolean>(false);
  const [includeDb, setIncludeDb] = useState<boolean>(false);
  const [dbContribution, setDbContribution] = useState<string>("");
  const [salaryStrategy, setSalaryStrategy] = useState<ReasonableSalaryStrategy>("Defensible");

  const params = useMemo(
    () => ({
      childrenOnPayroll: childrenOnPayroll === "" ? undefined : Number(childrenOnPayroll),
      wagePerChild: wagePerChild === "" ? undefined : Number(wagePerChild),
      wiPte,
      includeDb,
      dbContribution: dbContribution === "" ? undefined : Number(dbContribution),
      salaryStrategy,
    }),
    [childrenOnPayroll, wagePerChild, wiPte, includeDb, dbContribution, salaryStrategy],
  );
  const comparison = useEntityComparison(params);

  const scenarios = comparison.data ?? [];
  const blocked = scenarios.length > 0 && scenarios.every((s) => s.isBlocked);
  const playable = scenarios.filter((s) => !s.isBlocked);
  const cheapest = playable.reduce<EntityScenarioResult | null>(
    (best, s) =>
      best == null || s.projectedTotalCost < best.projectedTotalCost ? s : best,
    null,
  );

  const childCount = family.data?.children.length ?? 0;
  const sCorpScenario = playable.find((s) => s.structure.includes("S-Corp election"));
  const salaryRange = sCorpScenario?.reasonableSalaryRange ?? null;

  return (
    <>
      <PageHeader
        eyebrow="Strategy / Entity"
        title="Entity Transition Center"
        description="Side-by-side total household tax across Sole-Prop, S-Corp, S-Corp + FMLLC, optional S-Corp + Defined Benefit Plan, and C-Corp. Each scenario runs the same engine that powers your real projection — knobs toggle the WI Pass-Through Entity election (SALT-cap workaround), the salary tier, and the DB plan column."
        actions={
          cheapest && (
            <Badge tone="success" className="gap-1">
              <TrendingDown className="h-3 w-3" />
              Lowest total cost: {cheapest.structure.split(" (")[0]}
            </Badge>
          )
        }
      />

      {blocked && (
        <Card className="mb-6 border-amber-500/60 bg-amber-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <AlertCircle className="h-4 w-4 text-amber-400" />
              Comparison blocked
            </CardTitle>
            <CardDescription>
              {scenarios[0].blockingReason ??
                "Add Tax Intake → Business YTD before we can compare entity structures."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/tax-intake">
              <Button size="sm">Open Tax Intake</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Sparkles className="h-4 w-4 text-primary" />
              Hire-kids levers
            </CardTitle>
            <CardDescription>
              Sole-prop and FMLLC scenarios become more attractive when children are on payroll.
              Your family profile lists {childCount} {childCount === 1 ? "child" : "children"}.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Children on payroll (override)">
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                max={10}
                value={childrenOnPayroll}
                onChange={(e) => setChildrenOnPayroll(e.target.value)}
                placeholder={String(family.data?.children.filter((c) => c.isOnPayroll).length ?? 0)}
              />
            </Field>
            <Field label="Annual wage per child">
              <Input
                type="number"
                inputMode="decimal"
                value={wagePerChild}
                onChange={(e) => setWagePerChild(e.target.value)}
              />
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">S-Corp tuning</CardTitle>
            <CardDescription>
              Apply the WI Pass-Through Entity election (Wis. Stat. § 71.21(6)) to convert state
              tax on distributions into a fully-deductible business expense (escapes the $10k SALT
              cap). Pick a salary tier anchored to BLS OEWS Wisconsin software developer wages.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-medium">WI Pass-Through Entity election</div>
                <div className="text-xs text-muted-foreground">
                  Re-elect annually; election due within 90 days of year-end for prior year.
                </div>
              </div>
              <Switch checked={wiPte} onCheckedChange={setWiPte} />
            </div>
            <Field label="Reasonable salary strategy">
              <Select
                value={salaryStrategy}
                onChange={(e) => setSalaryStrategy(e.target.value as ReasonableSalaryStrategy)}
              >
                <option value="Low">Low — high audit risk (≥ BLS WI P25 ~$75k)</option>
                <option value="Defensible">Defensible — BLS WI P50 ~$108k (recommended)</option>
                <option value="Aggressive">Aggressive — ≥ BLS WI P75 ~$138k (audit-safe)</option>
              </Select>
            </Field>
            {salaryRange && (
              <div className="rounded-md border border-border/60 bg-muted/30 p-2 text-xs">
                <div className="font-semibold text-foreground">Salary range for your numbers</div>
                <div className="mt-1 grid grid-cols-3 gap-2">
                  <RangeCell label="Low" amount={salaryRange.lowAuditRisk} active={salaryStrategy === "Low"} />
                  <RangeCell label="Defensible" amount={salaryRange.defensible} active={salaryStrategy === "Defensible"} />
                  <RangeCell label="Aggressive" amount={salaryRange.aggressiveAuditRisk} active={salaryStrategy === "Aggressive"} />
                </div>
                <div className="mt-2 text-muted-foreground">{salaryRange.citation}</div>
                <div className="text-muted-foreground/70">Source: {salaryRange.source}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-sm">Defined Benefit / Cash Balance plan</CardTitle>
          <CardDescription>
            Adds a 5th column showing what an S-Corp + DB plan looks like. DB plans are the largest
            legal pre-tax shelter for high-income solos ($100k–$300k+/yr deductible) but require
            annual funding, an actuary (~$2.5k/yr), and a multi-year commitment.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-medium">Model the DB plan column</div>
              <div className="text-xs text-muted-foreground">Off by default — opt-in only.</div>
            </div>
            <Switch checked={includeDb} onCheckedChange={setIncludeDb} />
          </div>
          <Field label="DB contribution override (annual $)">
            <Input
              type="number"
              inputMode="decimal"
              value={dbContribution}
              onChange={(e) => setDbContribution(e.target.value)}
              placeholder="leave blank for engine estimate (~30% of distributable, capped $300k)"
              disabled={!includeDb}
            />
          </Field>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
        {comparison.isLoading && (
          <>
            <Skeleton className="h-72 w-full" />
            <Skeleton className="h-72 w-full" />
            <Skeleton className="h-72 w-full" />
            <Skeleton className="h-72 w-full" />
            <Skeleton className="h-72 w-full" />
          </>
        )}
        {playable.map((s) => (
          <ScenarioCard key={s.structure} scenario={s} isCheapest={cheapest?.structure === s.structure} />
        ))}
      </div>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>How to read this</CardTitle>
          <CardDescription>
            Each scenario nets all of: federal income, state income, SE/FICA, QBI, payroll cost
            (Gusto + WI UI + FUTA + WC), and admin overhead.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">Sole-prop SMLLC</strong> is the default for year 1.
            Lowest admin overhead. Kids on parent-owned sole-prop payroll are FICA-exempt up to age
            18.
          </p>
          <p>
            <strong className="text-foreground">S-Corp election</strong> saves SE tax on
            distributions above a reasonable salary, at the cost of payroll, an 1120-S, and an
            accountable plan. Filing window: Form 2553 by 3/15 for current-year effect; Rev Proc
            2013-30 grants automatic late relief within 3y 75d.
          </p>
          <p>
            <strong className="text-foreground">S-Corp + FMLLC</strong> restores the FICA
            exemption for kids by hiring them through the FMLLC, while keeping the S-Corp&apos;s SE
            savings on distributions.
          </p>
          <p>
            <strong className="text-foreground">S-Corp + DB plan</strong> stacks a defined-benefit
            shelter on top — biggest pre-tax bucket for high earners but commits to annual funding.
          </p>
          <p>
            <strong className="text-foreground">C-Corp</strong> is rarely the right answer for a
            cash-out solo contractor. Useful only when retaining substantial capital inside the
            corp.
          </p>
          <p>
            <strong className="text-foreground">WI PTE election</strong>: when toggled, the entity
            pays 7.9% WI tax on pass-through distributions; you get a refundable credit on your
            personal return AND the entity-level state tax becomes a fully-deductible business
            expense (escapes the $10k SALT cap). The federal benefit is shown on each S-Corp card.
          </p>
        </CardContent>
      </Card>

      <Card className="mt-4 border-blue-500/40 bg-blue-500/5">
        <CardHeader>
          <CardTitle className="text-sm">Missed 3/15? Late S-Corp election</CardTitle>
          <CardDescription>
            Rev Proc 2013-30 grants automatic relief if Form 2553 is filed within 3 years 75 days
            of the requested effective date with a reasonable-cause statement. Download a draft
            cover letter pre-filled with your business profile.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <a
            href="/api/strategy/late-scorp-election-letter"
            target="_blank"
            rel="noreferrer"
          >
            <Button size="sm" variant="outline" className="gap-1">
              <Download className="h-4 w-4" />
              Download draft Rev Proc 2013-30 letter
            </Button>
          </a>
        </CardContent>
      </Card>
    </>
  );
}

function RangeCell({ label, amount, active }: { label: string; amount: number; active: boolean }) {
  return (
    <div className={`rounded border p-1.5 text-center ${active ? "border-primary bg-primary/10" : "border-border/40"}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold">{formatMoney(amount)}</div>
    </div>
  );
}

function ScenarioCard({
  scenario,
  isCheapest,
}: {
  scenario: EntityScenarioResult;
  isCheapest: boolean;
}) {
  return (
    <Card className={isCheapest ? "border-emerald-500/60" : undefined}>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-sm">{scenario.structure}</CardTitle>
            <CardDescription>{recommendationLabel(scenario.recommendation)}</CardDescription>
          </div>
          {isCheapest && <Badge tone="success">Lowest</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div>
          <div className="text-xs text-muted-foreground">Total cost (tax + admin + payroll)</div>
          <div className="text-2xl font-semibold">{formatMoney(scenario.projectedTotalCost)}</div>
        </div>
        <Row label="Marginal business tax" value={formatMoney(scenario.projectedTotalTax)} />
        <Row label="Admin cost / yr" value={formatMoney(scenario.projectedAdminCost)} />
        <Row label="SE / FICA" value={formatMoney(scenario.seOrFicaTax)} />
        <Row label="QBI deduction" value={formatMoney(scenario.qbiDeduction)} />
        {scenario.targetReasonableSalary != null && (
          <Row label="Owner W-2 salary" value={formatMoney(scenario.targetReasonableSalary)} />
        )}
        {scenario.payrollCostAnnual != null && (
          <Row label="Payroll cost" value={formatMoney(scenario.payrollCostAnnual)} />
        )}
        {scenario.wisconsinPteFederalSavings != null && (
          <Row
            label="WI PTE federal savings"
            value={`-${formatMoney(scenario.wisconsinPteFederalSavings)}`}
            highlight="positive"
          />
        )}
        {scenario.definedBenefitContribution != null && (
          <Row
            label="DB contribution"
            value={formatMoney(scenario.definedBenefitContribution)}
            highlight="positive"
          />
        )}
        <p className="pt-2 text-xs text-muted-foreground">{scenario.summary}</p>
        {scenario.requiredActions.length > 0 && (
          <div className="pt-2">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Required to switch
            </div>
            <ul className="space-y-1 text-xs">
              {scenario.requiredActions.map((a) => (
                <li key={a} className="flex items-start gap-1.5">
                  <ChevronRight className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                  {a}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: "positive";
}) {
  const valueClass =
    highlight === "positive" ? "font-semibold text-emerald-500" : "font-medium";
  return (
    <div className="flex items-center justify-between border-b border-border/40 pb-1.5">
      <span className="text-muted-foreground">{label}</span>
      <span className={valueClass}>{value}</span>
    </div>
  );
}

function recommendationLabel(r: string) {
  switch (r) {
    case "Default":
      return "Default for year 1";
    case "Year2":
      return "Recommended year 2";
    case "Year2Plus":
      return "Year 2+ once kids are on payroll";
    case "OnlyForRetainedCapital":
      return "Only for retained capital plays";
    case "NotRecommended":
      return "Not recommended";
    default:
      return r;
  }
}
